import React from "react";

import Interweave from "interweave";
import _ from "lodash";

import config from "@/environment";
import { LoadingSlide } from "@components/title-slide-view";
import { BILLING_DETAILS_QUERY } from "@features/billing/queries";
import { useNorthbeamQuery } from "@hooks/http";
import { type Billing as BillingQuery } from "@north-beam/nb-client-side";
import { Elements } from "@stripe/react-stripe-js";
import { type PaymentMethod } from "@stripe/stripe-js";
import { loadStripe } from "@stripe/stripe-js/pure";

import { AddPaymentMethodForm } from "./add-payment-method-form";

function formatCard(paymentMethod: PaymentMethod) {
  const { card } = paymentMethod;
  if (!card) {
    return "Unknown payment method";
  }

  return `${_.capitalize(card.brand)} ending in ${card.last4} (expires ${
    card.exp_month
  }/${card.exp_year})`;
}

const ELEMENTS_OPTIONS = {
  fonts: [
    {
      cssSrc: "https://rsms.me/inter/inter.css",
    },
    {
      cssSrc:
        "https://fonts.googleapis.com/css?family=Open+Sans:98,400,500,600&display=swap",
    },
  ],
};

export interface BillingFormProps {
  readonly onSuccess?: () => void;
}

export function BillingForm({ onSuccess }: BillingFormProps) {
  const { data, loading, error } = useNorthbeamQuery<BillingQuery>(
    BILLING_DETAILS_QUERY,
  );
  const billingDetails = data?.me?.billingDetails;

  let stripePromise = undefined;
  try {
    stripePromise = loadStripe(config.stripePublishableKey);
  } catch (e) {
    // ignore error
  }

  if (!stripePromise) {
    return null;
  }

  if (loading) {
    return <LoadingSlide />;
  }

  return (
    <Elements options={ELEMENTS_OPTIONS} stripe={stripePromise}>
      <p>
        We encourage ACH payments - If you would like to set up ACH, please
        email us at{" "}
        <a href="mailto:finance@northbeam.io">finance@northbeam.io</a>.
      </p>
      {loading ? (
        <LoadingSlide />
      ) : error ? (
        <p className="text-error">
          There was an error retrieving your billing information.
        </p>
      ) : billingDetails.billingUserId ? (
        <div>
          Billing handled in a different account.{" "}
          <a href={`/${billingDetails.billingUserId}/settings`}>
            Click here to switch accounts
          </a>
        </div>
      ) : !billingDetails.stripePaymentMethod ? (
        <div className="flex flex-col gap-4">
          <Interweave content="Please add a credit card below." />
          <AddPaymentMethodForm onSuccess={onSuccess} />
        </div>
      ) : (
        <div className="flex flex-col gap-4">
          <p>
            Your payment information is on file with us:{" "}
            <b>{formatCard(billingDetails.stripePaymentMethod)}</b>
          </p>
          <div>
            You can use this form to replace your credit card on file.
            <AddPaymentMethodForm onSuccess={onSuccess} />
          </div>
        </div>
      )}
    </Elements>
  );
}
